import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calculator, Clock, Activity, TrendingDown } from 'lucide-react';
import { FailureEvent, MTBFAnalysis } from '../types/analytics.types';
import { useAuth } from '../contexts/AuthContext';
import { getMtbfSeverityColor } from '../utils/colorHelpers';

const MTBFCalculator: React.FC = () => {
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [systemName, setSystemName] = useState('');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [totalOperatingTime, setTotalOperatingTime] = useState(0);
    const [failureEvents, setFailureEvents] = useState<FailureEvent[]>([]);
    const [analysis, setAnalysis] = useState<MTBFAnalysis | null>(null);

    const addFailureEvent = () => {
        const newEvent: FailureEvent = {
            id: `fe-${Date.now()}`,
            timestamp: new Date(),
            failureMode: '',
            severity: 'Minor',
            downtime: 0,
            repairTime: 0,
            cost: 0,
            systemId: '',
            systemName: systemName,
        };
        setFailureEvents([...failureEvents, newEvent]);
    };

    const updateFailureEvent = (id: string, field: keyof FailureEvent, value: FailureEvent[keyof FailureEvent]) => {
        setFailureEvents(events =>
            events.map(e => (e.id === id ? { ...e, [field]: value } : e))
        );
    };

    const removeFailureEvent = (id: string) => {
        setFailureEvents(events => events.filter(e => e.id !== id));
    };

    const calculateMTBF = () => {
        if (failureEvents.length === 0) {
            alert('Please add at least one failure event');
            return;
        }

        if (totalOperatingTime <= 0) {
            alert('Please enter total operating time');
            return;
        }

        const totalFailures = failureEvents.length;
        const mtbf = totalOperatingTime / totalFailures;
        const failureRate = 1 / mtbf; // Failures per hour (λ)

        // Calculate MTTR
        const totalRepairTime = failureEvents.reduce((sum, e) => sum + e.repairTime, 0);
        const mttr = totalRepairTime / totalFailures;

        // Calculate total downtime
        const totalDowntime = failureEvents.reduce((sum, e) => sum + e.downtime, 0);

        // Calculate availability
        const availability = (mtbf / (mtbf + mttr)) * 100;

        // Failure distribution
        const failuresByMode: Record<string, number> = {};
        const failuresBySeverity: Record<string, number> = {
            'Critical': 0,
            'Major': 0,
            'Minor': 0,
        };

        failureEvents.forEach(event => {
            // By mode
            failuresByMode[event.failureMode] = (failuresByMode[event.failureMode] || 0) + 1;

            // By severity
            failuresBySeverity[event.severity]++;
        });

        // Cost metrics
        const totalRepairCost = failureEvents.reduce((sum, e) => sum + e.cost, 0);
        const averageCostPerFailure = totalRepairCost / totalFailures;

        // Reliability function R(t) = e^(-λt)
        const reliability = (t: number) => Math.exp(-failureRate * t);

        const mtbfAnalysis: MTBFAnalysis = {
            id: `mtbf-${Date.now()}`,
            title,
            systemName,
            analysisStartDate: startDate,
            analysisEndDate: endDate,
            failureEvents,
            totalFailures,
            totalOperatingTime,
            mtbf,
            failureRate,
            mttr,
            totalDowntime,
            availability,
            reliability,
            failuresByMode,
            failuresBySeverity,
            totalRepairCost,
            averageCostPerFailure,
            organizationId: user?.organizationId || '',
            createdBy: user?.uid || '',
            createdByName: user?.displayName || user?.email || '',
            createdAt: new Date(),
            updatedAt: new Date(),
        };

        setAnalysis(mtbfAnalysis);
    };

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Analytics
                </Link>

                <div>
                    <h1 className="text-3xl font-bold text-gray-900">MTBF/MTTR Calculator</h1>
                    <p className="text-gray-600 mt-1">Mean Time Between Failures & Mean Time To Repair Analysis</p>
                </div>
            </div>

            {/* Input Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Analysis Information</h2>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Analysis Title *
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Pump System 2026 Analysis"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            System Name *
                        </label>
                        <input
                            type="text"
                            value={systemName}
                            onChange={(e) => setSystemName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Hydraulic Pump Assembly"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Analysis Start Date
                        </label>
                        <input
                            type="date"
                            value={startDate.toISOString().split('T')[0]}
                            onChange={(e) => setStartDate(new Date(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Analysis End Date
                        </label>
                        <input
                            type="date"
                            value={endDate.toISOString().split('T')[0]}
                            onChange={(e) => setEndDate(new Date(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Operating Time (hours) *
                        </label>
                        <input
                            type="number"
                            value={totalOperatingTime}
                            onChange={(e) => setTotalOperatingTime(parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="0"
                        />
                    </div>
                </div>
            </div>

            {/* Failure Events */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Failure Events</h2>
                    <button
                        onClick={addFailureEvent}
                        className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add Failure
                    </button>
                </div>

                {failureEvents.length > 0 ? (
                    <div className="space-y-4">
                        {failureEvents.map((event, index) => (
                            <div key={event.id} className="p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-start justify-between mb-4">
                                    <h3 className="font-medium text-gray-900">Failure #{index + 1}</h3>
                                    <button
                                        onClick={() => removeFailureEvent(event.id)}
                                        className="text-red-600 hover:text-red-700"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="grid md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Failure Mode *
                                        </label>
                                        <input
                                            type="text"
                                            value={event.failureMode}
                                            onChange={(e) => updateFailureEvent(event.id, 'failureMode', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., Bearing seizure"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Severity
                                        </label>
                                        <select
                                            value={event.severity}
                                            onChange={(e) => updateFailureEvent(event.id, 'severity', e.target.value as 'Critical' | 'Major' | 'Minor')}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="Minor">Minor</option>
                                            <option value="Major">Major</option>
                                            <option value="Critical">Critical</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Timestamp
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={event.timestamp instanceof Date ? event.timestamp.toISOString().slice(0, 16) : ''}
                                            onChange={(e) => updateFailureEvent(event.id, 'timestamp', new Date(e.target.value))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Downtime (hours)
                                        </label>
                                        <input
                                            type="number"
                                            value={event.downtime}
                                            onChange={(e) => updateFailureEvent(event.id, 'downtime', parseFloat(e.target.value))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Repair Time (hours)
                                        </label>
                                        <input
                                            type="number"
                                            value={event.repairTime}
                                            onChange={(e) => updateFailureEvent(event.id, 'repairTime', parseFloat(e.target.value))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Repair Cost ($)
                                        </label>
                                        <input
                                            type="number"
                                            value={event.cost}
                                            onChange={(e) => updateFailureEvent(event.id, 'cost', parseFloat(e.target.value))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                        <p className="text-gray-600 mb-4">No failure events recorded yet</p>
                        <button
                            onClick={addFailureEvent}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Add First Failure Event
                        </button>
                    </div>
                )}

                {failureEvents.length > 0 && (
                    <div className="mt-6 flex justify-center">
                        <button
                            onClick={calculateMTBF}
                            disabled={failureEvents.length === 0 || totalOperatingTime <= 0}
                            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Calculator className="w-5 h-5" />
                            Calculate MTBF/MTTR
                        </button>
                    </div>
                )}
            </div>

            {/* Results */}
            {analysis && (
                <div className="space-y-6">
                    {/* Key Metrics */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h2>
                        <div className="grid md:grid-cols-4 gap-4">
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm text-blue-600">MTBF</span>
                                </div>
                                <div className="text-3xl font-bold text-blue-900">{analysis.mtbf.toFixed(1)}</div>
                                <div className="text-sm text-blue-700">hours</div>
                            </div>

                            <div className="p-4 bg-orange-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <Activity className="w-5 h-5 text-orange-600" />
                                    <span className="text-sm text-orange-600">MTTR</span>
                                </div>
                                <div className="text-3xl font-bold text-orange-900">{analysis.mttr.toFixed(1)}</div>
                                <div className="text-sm text-orange-700">hours</div>
                            </div>

                            <div className="p-4 bg-green-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingDown className="w-5 h-5 text-green-600" />
                                    <span className="text-sm text-green-600">Availability</span>
                                </div>
                                <div className="text-3xl font-bold text-green-900">{analysis.availability.toFixed(1)}%</div>
                                <div className="text-sm text-green-700">uptime</div>
                            </div>

                            <div className="p-4 bg-red-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <Activity className="w-5 h-5 text-red-600" />
                                    <span className="text-sm text-red-600">Failure Rate (λ)</span>
                                </div>
                                <div className="text-3xl font-bold text-red-900">{(analysis.failureRate * 1000).toFixed(3)}</div>
                                <div className="text-sm text-red-700">per 1000 hrs</div>
                            </div>
                        </div>
                    </div>

                    {/* Failure Distribution */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Failures by Severity</h3>
                            <div className="space-y-3">
                                {Object.entries(analysis.failuresBySeverity).map(([severity, count]) => (
                                    <div key={severity} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className={`px-3 py-1 text-sm rounded-full ${getMtbfSeverityColor(severity)}`}>
                                                {severity}
                                            </span>
                                        </div>
                                        <div className="text-lg font-semibold text-gray-900">{count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Metrics</h3>
                            <div className="space-y-4">
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">Total Repair Cost</div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        ${analysis.totalRepairCost.toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">Average Cost per Failure</div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        ${analysis.averageCostPerFailure.toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600 mb-1">Total Downtime</div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        {analysis.totalDowntime.toFixed(1)} hrs
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Failure Modes */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Failure Modes</h3>
                        <div className="space-y-2">
                            {Object.entries(analysis.failuresByMode)
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 5)
                                .map(([mode, count]) => (
                                    <div key={mode} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-gray-700">{mode || 'Not specified'}</span>
                                        <span className="font-semibold text-gray-900">{count} failures</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MTBFCalculator;
